import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowRight, 
  Calendar,
  BookOpen,
  Moon,
  Sun,
  Check,
  Sparkles,
  Coffee,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { retreatContent } from '../data/retreatContent';

const OnboardingFlow = ({ onComplete, onSetStartDate }) => {
  const [step, setStep] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const containerRef = useRef(null);

  // Check for desktop
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  const steps = [
    // Welcome
    {
      title: 'Welcome to Your Lenten Journey',
      content: (
        <div className="text-center space-y-8 max-w-lg mx-auto">
          <img 
            src={retreatContent.logo}
            alt="DMU"
            className="w-28 h-28 mx-auto rounded-full shadow-lg"
          />
          <div>
            <h2 className="font-heading text-2xl lg:text-3xl text-[var(--primary)] font-bold mb-3">
              30-Day Ignatian Lenten Journey to Flourishing
            </h2>
            <p className="text-[var(--text-muted)] text-base lg:text-lg">
              Divine Mercy University
            </p>
          </div>
          <p className="text-[var(--foreground)] text-base lg:text-lg leading-relaxed">
            Based on the <strong>Spiritual Exercises of St. Ignatius</strong>, 
            the <strong>19th Annotation</strong>, and <strong>The RIMP Way</strong> — this retreat helps you discover God's unique plan for your life.
          </p>
          <div className="bg-[var(--secondary)]/10 rounded-xl p-5 border border-[var(--secondary)]/30">
            <p className="text-[var(--foreground)] text-base lg:text-lg">
              <Sparkles className="w-5 h-5 inline mr-2 text-[var(--secondary)]" />
              <strong>A retreat in daily life:</strong> Designed for busy people 
              who can't leave their responsibilities — pray at home, work, or anywhere.
            </p>
          </div>
        </div>
      )
    },
    // What is RIMP?
    {
      title: 'The RIMP Framework',
      content: (
        <div className="space-y-6 max-w-lg mx-auto">
          <p className="text-[var(--foreground)] text-base lg:text-lg text-center mb-6">
            <strong>RIMP</strong> maps the four "weeks" of the Spiritual Exercises to your journey of flourishing:
          </p>
          <div className="space-y-4 flex flex-col items-center">
            <div className="w-full max-w-md flex items-center gap-4 bg-[#5C4A68]/10 rounded-xl p-4 border border-[#5C4A68]/20">
              <div className="w-14 h-14 rounded-full bg-[#5C4A68] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">R</span>
              </div>
              <div>
                <h4 className="font-heading font-semibold text-[#5C4A68] text-lg">Relationship</h4>
                <p className="text-base text-[var(--foreground)]">Who is God to me? (Days 1-7)</p>
              </div>
            </div>
            <div className="w-full max-w-md flex items-center gap-4 bg-[var(--secondary)]/10 rounded-xl p-4 border border-[var(--secondary)]/20">
              <div className="w-14 h-14 rounded-full bg-[var(--secondary)] flex items-center justify-center flex-shrink-0">
                <span className="text-[var(--foreground)] font-bold text-xl">I</span>
              </div>
              <div>
                <h4 className="font-heading font-semibold text-[var(--foreground)] text-lg">Identity</h4>
                <p className="text-base text-[var(--foreground)]">Who did God make me to be? (Days 8-18)</p>
              </div>
            </div>
            <div className="w-full max-w-md flex items-center gap-4 bg-[#4A3B52]/10 rounded-xl p-4 border border-[#4A3B52]/20">
              <div className="w-14 h-14 rounded-full bg-[#4A3B52] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">M</span>
              </div>
              <div>
                <h4 className="font-heading font-semibold text-[#4A3B52] text-lg">Mission</h4>
                <p className="text-base text-[var(--foreground)]">What is God calling me to do? (Days 19-25)</p>
              </div>
            </div>
            <div className="w-full max-w-md flex items-center gap-4 bg-[#5C4A68]/10 rounded-xl p-4 border border-[#5C4A68]/20">
              <div className="w-14 h-14 rounded-full bg-[#5C4A68] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">P</span>
              </div>
              <div>
                <h4 className="font-heading font-semibold text-[#5C4A68] text-lg">Purpose</h4>
                <p className="text-base text-[var(--foreground)]">Why am I here? (Days 26-30)</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Daily Practice
    {
      title: 'Your Daily Practice',
      content: (
        <div className="space-y-6 max-w-lg mx-auto">
          <p className="text-[var(--foreground)] text-base lg:text-lg text-center">
            <em>Flexible timing — pray when it works best for you</em>
          </p>
          <p className="text-[var(--foreground)] text-base lg:text-lg text-center">
            Each day follows a rhythm of prayer and reflection (about 45 minutes total):
          </p>
          <div className="space-y-4 flex flex-col items-center">
            <div className="w-full max-w-md flex items-start gap-4 bg-[var(--secondary)]/10 rounded-xl p-4">
              <Sun className="w-8 h-8 text-[var(--secondary)] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-[var(--foreground)] text-lg">Morning Prayer</h4>
                <p className="text-base text-[var(--text-muted)]">Scripture reading, reflection, and journaling (20-25 min)</p>
              </div>
            </div>
            <div className="w-full max-w-md flex items-start gap-4 bg-[var(--primary)]/10 rounded-xl p-4">
              <Coffee className="w-8 h-8 text-[var(--primary)] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-[var(--foreground)] text-lg">Midday Check-In</h4>
                <p className="text-base text-[var(--text-muted)]">Brief awareness exercise (5 min)</p>
              </div>
            </div>
            <div className="w-full max-w-md flex items-start gap-4 bg-[var(--primary-dark)]/10 rounded-xl p-4">
              <Moon className="w-8 h-8 text-[var(--primary-dark)] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-[var(--foreground)] text-lg">Evening Examen</h4>
                <p className="text-base text-[var(--text-muted)]">Review your day with God (15 min)</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // What You'll Need
    {
      title: "What You'll Need",
      content: (
        <div className="space-y-6 max-w-lg mx-auto">
          <div className="bg-[var(--muted)] rounded-xl p-6 border border-[var(--border)]">
            <h4 className="font-heading font-semibold text-[var(--foreground)] text-lg mb-4 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[var(--primary)]" />
              Essentials
            </h4>
            <ul className="space-y-3 text-base text-[var(--foreground)]">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[var(--secondary)] flex-shrink-0 mt-0.5" />
                <span>This app (you're ready!)</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[var(--secondary)] flex-shrink-0 mt-0.5" />
                <span>A quiet place for prayer</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[var(--secondary)] flex-shrink-0 mt-0.5" />
                <span>Your Bible (or use the app's readings)</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[var(--secondary)] flex-shrink-0 mt-0.5" />
                <span>Openness to God's voice</span>
              </li>
            </ul>
          </div>
          <div className="bg-[var(--primary)]/5 rounded-xl p-6 border border-[var(--primary)]/20">
            <h4 className="font-heading font-semibold text-[var(--primary)] text-lg mb-3 flex items-center gap-2">
              <Sparkles className="w-6 h-6" />
              Optional Enhancements
            </h4>
            <ul className="space-y-2 text-base text-[var(--foreground)]">
              <li>• A physical journal for deeper reflection</li>
              <li>• RC Spirituality app for more meditations</li>
              <li>• A spiritual director for guidance</li>
            </ul>
          </div>
        </div>
      )
    },
    // Set Start Date
    {
      title: 'When Will You Begin?',
      content: (
        <div className="space-y-8 max-w-md mx-auto text-center">
          <p className="text-[var(--foreground)] text-base lg:text-lg">
            Choose your retreat start date. Many begin on <strong>Ash Wednesday</strong>, 
            but you can start any day.
          </p>
          <div className="max-w-xs mx-auto bg-white rounded-xl p-5 border-2 border-[var(--primary)]/20 shadow-sm">
            <label className="block text-base font-medium text-[var(--primary)] mb-3 flex items-center justify-center gap-2">
              <Calendar className="w-5 h-5" />
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-3 border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] text-center text-lg"
            />
          </div>
          <div className="flex gap-3 justify-center max-w-xs mx-auto">
            <button
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setStartDate(today);
              }}
              className="flex-1 py-3 px-4 bg-[var(--primary)]/10 text-[var(--primary)] rounded-lg text-base font-medium hover:bg-[var(--primary)]/20 transition-colors"
            >
              Start Today
            </button>
            <button
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                setStartDate(tomorrow.toISOString().split('T')[0]);
              }}
              className="flex-1 py-3 px-4 bg-[var(--primary)]/10 text-[var(--primary)] rounded-lg text-base font-medium hover:bg-[var(--primary)]/20 transition-colors"
            >
              Tomorrow
            </button>
          </div>
          <p className="text-sm text-[var(--text-muted)]">
            You can always change this later in your Profile.
          </p>
        </div>
      )
    },
    // Ready to Begin (removed Quick Tips)
    {
      title: "You're Ready!",
      content: (
        <div className="text-center space-y-8 max-w-lg mx-auto">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--primary-dark)] flex items-center justify-center shadow-lg">
            <Check className="w-12 h-12 text-white" />
          </div>
          <div>
            <h3 className="font-heading text-2xl text-[var(--primary)] font-bold mb-3">
              Your Journey Awaits
            </h3>
            <p className="text-[var(--foreground)] text-base lg:text-lg leading-relaxed">
              Over the next 30 days, you'll grow in your relationship with God 
              and discover His unique plan for your flourishing.
            </p>
          </div>
          <div className="bg-[var(--secondary)]/10 rounded-xl p-5 border border-[var(--secondary)]/30">
            <p className="text-[var(--foreground)] text-base italic">
              "Be still and know that I am God." — Psalm 46:10
            </p>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleComplete = () => {
    if (startDate) {
      onSetStartDate(startDate);
    }
    onComplete();
  };

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && step < steps.length - 1) {
      handleNext();
    }
    if (isRightSwipe && step > 0) {
      handlePrev();
    }
  };

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;
  const isDateStep = step === 4;

  return (
    <div className="fixed inset-0 z-50 bg-[var(--background)] flex flex-col">
      {/* Progress Dots */}
      <div className="px-4 pt-8 pb-4">
        <div className="flex justify-center gap-2">
          {steps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setStep(idx)}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === step 
                  ? 'bg-[var(--primary)] w-8' 
                  : idx < step 
                    ? 'bg-[var(--primary)]/50' 
                    : 'bg-[var(--border)]'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Title */}
      <div className="px-6 py-4">
        <h1 className="font-heading text-2xl lg:text-3xl text-[var(--primary)] font-bold text-center">
          {currentStep.title}
        </h1>
      </div>

      {/* Swipeable Content */}
      <div 
        ref={containerRef}
        className="flex-1 px-6 py-4 overflow-y-auto"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {currentStep.content}
      </div>

      {/* Bottom Navigation */}
      <div className="p-4 pb-8 bg-white border-t border-[var(--border)]">
        {/* Swipe hint - only on mobile */}
        {!isLastStep && !isDesktop && (
          <p className="swipe-hint text-center text-sm text-[var(--text-muted)] mb-4">
            ← Swipe to navigate →
          </p>
        )}
        
        <div className="flex items-center gap-3 max-w-md mx-auto">
          {/* Back button */}
          {step > 0 && (
            <button
              onClick={handlePrev}
              className="w-12 h-12 rounded-full border-2 border-[var(--border)] flex items-center justify-center hover:bg-[var(--muted)] transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-[var(--foreground)]" />
            </button>
          )}
          
          {/* Main action button */}
          <button
            onClick={isLastStep ? handleComplete : handleNext}
            disabled={isDateStep && !startDate}
            className="flex-1 py-4 bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
          >
            {isLastStep ? (
              <>
                Begin My Retreat
                <ArrowRight className="w-5 h-5" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
        
        {/* Skip option */}
        {!isLastStep && step > 0 && (
          <button
            onClick={handleComplete}
            className="w-full py-3 text-[var(--text-muted)] text-sm mt-3"
          >
            Skip intro
          </button>
        )}
      </div>
    </div>
  );
};

export default OnboardingFlow;
